'use client'

import { useState } from 'react'
import Link from 'next/link'
import { Users } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import type { TeamMember } from '@/lib/types/team'
import type { UserSummary } from '@/lib/services/productService'

// ============================================================================
// TYPES
// ============================================================================

interface ProductTeamSectionProps {
  owner: UserSummary
  members: TeamMember[]
  isOwner?: boolean
  onManageTeam?: () => void
}

// ============================================================================
// COMPONENT
// ============================================================================

export function ProductTeamSection({
  owner,
  members,
  isOwner = false,
  onManageTeam,
}: ProductTeamSectionProps) {
  const [showAllMembers, setShowAllMembers] = useState(false)

  // Show max 4 member avatars in collapsed view
  const visibleMembers = showAllMembers ? members : members.slice(0, 4)
  const hiddenCount = members.length - 4

  return (
    <div className="px-6 md:px-8 py-4 border-t border-border">
      {/* Owner Section */}
      <div className="flex items-center justify-between">
        <Link
          href={owner.username ? `/u/${owner.username}` : '#'}
          className="flex items-center gap-3 hover:opacity-80 transition-opacity"
        >
          <Avatar className="h-10 w-10">
            {owner.avatarUrl && (
              <AvatarImage src={owner.avatarUrl} alt={owner.name} />
            )}
            <AvatarFallback>{owner.name.slice(0, 2).toUpperCase()}</AvatarFallback>
          </Avatar>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm text-foreground">{owner.name}</span>
              <span className="text-xs px-1.5 py-0.5 rounded-md bg-emerald-600/90 text-white font-medium">
                Основатель
              </span>
            </div>
            <span className="text-xs text-foreground-tertiary">Создатель продукта</span>
          </div>
        </Link>

        {/* Manage Team Button (Owner Only) */}
        {isOwner && onManageTeam && (
          <Button variant="outline" size="sm" onClick={onManageTeam}>
            <Users className="h-4 w-4 mr-2" />
            Управление командой
          </Button>
        )}
      </div>

      {/* Team Members Section */}
      {members.length > 0 && (
        <div className="mt-4 pt-4 border-t border-border/50">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-medium text-foreground-secondary uppercase tracking-wide">
              Команда ({members.length})
            </span>
            {members.length > 4 && !showAllMembers && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 text-xs"
                onClick={() => setShowAllMembers(true)}
              >
                Показать всех
              </Button>
            )}
          </div>

          <div className="flex flex-wrap gap-3">
            <TooltipProvider>
              {visibleMembers.map((member) => (
                <Tooltip key={member.userId}>
                  <TooltipTrigger asChild>
                    <Link
                      href={member.username ? `/u/${member.username}` : '#'}
                      className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                    >
                      <Avatar className="h-8 w-8">
                        {member.avatarUrl && (
                          <AvatarImage src={member.avatarUrl} alt={member.name} />
                        )}
                        <AvatarFallback className="text-xs">
                          {member.name.slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="hidden sm:block">
                        <div className="text-sm font-medium text-foreground">
                          {member.name}
                        </div>
                        <div className="text-xs text-foreground-tertiary">
                          {member.role}
                        </div>
                      </div>
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="font-medium">{member.name}</p>
                    <p className="text-xs text-muted-foreground">{member.role}</p>
                  </TooltipContent>
                </Tooltip>
              ))}

              {/* Hidden count indicator */}
              {!showAllMembers && hiddenCount > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 w-8 rounded-full p-0"
                      onClick={() => setShowAllMembers(true)}
                    >
                      +{hiddenCount}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Показать ещё {hiddenCount} участников</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </TooltipProvider>
          </div>

          {/* Collapse button when expanded */}
          {showAllMembers && members.length > 4 && (
            <Button
              variant="ghost"
              size="sm"
              className="mt-2 h-6 text-xs"
              onClick={() => setShowAllMembers(false)}
            >
              Свернуть
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
